import { useCallback, useEffect, useMemo, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import { PlusCircledIcon } from '@radix-ui/react-icons';
import {
  Archive,
  Bot,
  Check,
  Clock,
  Linkedin,
  MessageCircle,
  MessageSquare,
  Tag,
  TrashIcon,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { channelCapabilities, getChannelInfo } from '@/lib/channelCapabilities';
import {
  convertToDisplayFormat,
  fromTipTapDoc,
  toTipTapDoc,
} from '@/lib/mentionsConvert';
// Adjust relative paths for UI components
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
// Adjust relative path for MentionsEditor
import MentionsEditor from '@/components/settings/canned-messages/editor/MentionsEditor';

// Zod schema for validating weighted owner allocation
const weightedAllocationSchema = z
  .array(
    z.object({
      user_id: z.string().min(1, 'Owner is required'),
      allocation: z
        .number()
        .min(1, 'Allocation must be at least 1%')
        .max(100, 'Allocation cannot exceed 100%'),
    })
  )
  .min(1, 'At least one owner is required')
  .refine(
    (owners) => owners.reduce((sum, o) => sum + o.allocation, 0) === 100,
    { message: 'Total allocation must equal 100%' }
  );

// IMPORTANT: Pass the deletion handlers from WorkflowEditor
const ActionConfiguration = ({
  isActionTypeModalOpen,
  setIsActionTypeModalOpen,
  isActionConfigSheetOpen,
  setIsActionConfigSheetOpen,
  selectedActionNode,
  setSelectedActionNode,
  actionSourceNode, // ID of the node triggering the 'Add Step' modal
  setActionSourceNode,
  isBranchConfigOpen,
  setIsBranchConfigOpen,
  nodes, // Current nodes from WorkflowEditor state
  setNodes, // Setter from WorkflowEditor state
  edges, // Current edges from WorkflowEditor state
  setEdges, // Setter from WorkflowEditor state
  // runLayout is no longer needed as prop, handled by effect in editor
  handleActionDelete, // Deletion handler from WorkflowEditor
  handleDeleteBranchConfig, // Deletion handler from WorkflowEditor
}) => {
  // --- Options Definitions (Keep as is) ---
  const actionOptions = useMemo(
    () => [
      {
        type: 'send_message',
        label: 'Send Message',
        icon: MessageSquare,
        description: 'Send a message to the contact',
        bgClass: 'bg-primary/10',
        borderClass: 'border-primary/20',
        iconClass: 'text-primary',
      },
      {
        type: 'reply_comment',
        label: 'Reply to Comment',
        icon: MessageCircle,
        description: 'Reply to a comment on the post',
        bgClass: 'bg-primary/10',
        borderClass: 'border-primary/20',
        iconClass: 'text-primary',
      },
      {
        type: 'linkedin_reply_comment',
        label: 'LinkedIn Comment',
        icon: Linkedin,
        description: 'Reply to a comment on a LinkedIn post',
        bgClass: 'bg-blue-600/10',
        borderClass: 'border-blue-600/20',
        iconClass: 'text-blue-600',
      },
      {
        type: 'contact_add_tag',
        label: 'Add Contact Tag',
        icon: Tag,
        description: 'Add one or more tags to the contact',
        bgClass: 'bg-secondary/20',
        borderClass: 'border-secondary/30',
        iconClass: 'text-secondary-foreground',
      },
      {
        type: 'contact_remove_tag',
        label: 'Remove Contact Tag',
        icon: Tag,
        description: 'Remove one or more tags from the contact',
        bgClass: 'bg-destructive/10',
        borderClass: 'border-destructive/20',
        iconClass: 'text-destructive',
      },
      {
        type: 'contact_state_change',
        label: 'Change Contact State',
        icon: Archive,
        description: 'Change the state of the contact',
        bgClass: 'bg-primary/10',
        borderClass: 'border-primary/20',
        iconClass: 'text-primary',
      },
      {
        type: 'contact_change_owner',
        label: 'Change Contact Owner',
        icon: Users,
        description: 'Change the owner of the contact',
        bgClass: 'bg-secondary/20',
        borderClass: 'border-secondary/30',
        iconClass: 'text-secondary-foreground',
      },
      {
        type: 'trigger_webhook',
        label: 'Trigger Webhook',
        icon: Webhook,
        description: 'Send a webhook request to an external URL',
        bgClass: 'bg-secondary/20',
        borderClass: 'border-secondary/30',
        iconClass: 'text-secondary-foreground',
      },
      {
        type: 'ai_select_message',
        label: 'AI Pick',
        icon: Bot,
        description:
          'Revio AI will select the most appropriate message to send based on the conversation context',
        bgClass: 'bg-purple-500/10',
        borderClass: 'border-purple-500/20',
        iconClass: 'text-purple-500',
      },
      // Delay is handled separately in otherOptions
    ],
    []
  );

  const otherOptions = useMemo(
    () => [
      {
        type: 'branch',
        label: 'Branch',
        icon: Workflow,
        description: 'Add a conditional branch to your workflow',
        bgClass: 'bg-secondary/20',
        borderClass: 'border-secondary/30',
        iconClass: 'text-secondary-foreground',
      },
      {
        type: 'delay',
        label: 'Delay',
        icon: Clock,
        description: 'Add a delay before the next action',
        bgClass: 'bg-primary/10',
        borderClass: 'border-primary/20',
        iconClass: 'text-primary',
      },
    ],
    []
  );

  // --- State Definitions (Keep as is) ---
  // Action config state
  const [messageText, setMessageText] = useState('');
  const [commentText, setCommentText] = useState('');
  const [selectedAddTags, setSelectedAddTags] = useState([]);
  const [selectedRemoveTags, setSelectedRemoveTags] = useState([]);
  const [contactState, setContactState] = useState('open');
  const [contactOwner, setContactOwner] = useState('');
  const [ownerMode, setOwnerMode] = useState('single'); // 'single' or 'multiple'
  const [ownerAllocations, setOwnerAllocations] = useState([]); // Array of { user_id, allocation }
  const [delayValue, setDelayValue] = useState('');
  const [delayUnit, setDelayUnit] = useState('minutes');
  const [currentActionType, setCurrentActionType] = useState(null); // Type being configured
  const [filteredOtherOptions, setFilteredOtherOptions] =
    useState(otherOptions);

  // Branch config state
  const [branches, setBranches] = useState([]); // Branches loaded for editing
  const [tempBranches, setTempBranches] = useState([]); // Temp state while editing branches
  const [fieldOptions, setFieldOptions] = useState([]); // Dynamic field options for branch conditions

  const staticFieldOptions = useMemo(
    () => [
      {
        value: 'contact.user_id',
        label: 'Contact Owner',
        type: 'owner-select',
      },
      {
        value: 'contact.status',
        label: 'Contact Status',
        type: 'status-select',
      },
      { value: 'contact.tag_ids', label: 'Contact Tags', type: 'tag-select' },
      { value: 'contact.verified', label: 'Contact Verified', type: 'boolean' },
      {
        value: 'contact.followers_count',
        label: 'Contact Follower Count',
        type: 'number',
      },
      {
        value: 'contact.following_count',
        label: 'Contact Following Count',
        type: 'number',
      },
      {
        value: 'contact_messages._total_msgs_received',
        label: 'Total Messages Received',
        type: 'number',
      },
      {
        value: 'contact_messages._prev_message_received_timestamp',
        label: 'Previous Message Received Timestamp',
        type: 'number-unit-days',
      },
      {
        value: 'contact_messages._none_messages_received_within',
        label: 'No Messages Received Since Start',
        type: 'no-input',
      },
    ],
    []
  );

  // --- Data Fetching (Keep as is) ---
  const { owners, tags } = useInboxStore(
    useShallow((state) => ({
      owners:
        state.owners.filter(
          (owner) => owner.role !== 'deleted' && owner.role !== 'system'
        ) || [],
      tags:
        state.tags.filter(
          (tag) =>
            !['No-Tag-SBCCRM', 'Seen-Not-Responded', 'Not-Responded'].includes(
              tag._id
            )
        ) || [],
    }))
  );

  // Add webhook state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookMethod, setWebhookMethod] = useState('POST');
  const [aiMessages, setAiMessages] = useState([]);
  const [tempAiMessageDoc, setTempAiMessageDoc] = useState(() =>
    toTipTapDoc('')
  );
  const [aiCustomPrompt, setAiCustomPrompt] = useState('');

  // LinkedIn Comment action state
  const [linkedinActionComment, setLinkedinActionComment] = useState('');

  // --- Effects ---

  //   Update filtered options to include or exclude branch from options list
  useEffect(() => {
    let currentFilteredOptions = [...otherOptions]; // Start with all options

    if (actionSourceNode) {
      // Find the edge originating from the actionSourceNode
      const targetEdge = edges.find((edge) => edge.source === actionSourceNode);
      // Check if the target node ID is 'end'
      const isTargetEnd = targetEdge?.target?.includes('end');
      // Filter out the 'branch' option if the target is 'end'
      if (!isTargetEnd) {
        currentFilteredOptions = otherOptions.filter(
          (option) => option.type !== 'branch'
        );
      }
    }

    setFilteredOtherOptions(currentFilteredOptions);
  }, [actionSourceNode, edges, otherOptions]);

  // Delay unit options
  const delayUnitOptions = [
    { value: 'seconds', label: 'Seconds' },
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
  ];

  // Select Action Type (Opens Correct Config Sheet/Modal)
  const selectActionType = (actionType) => {
    setCurrentActionType(actionType);
    setIsActionTypeModalOpen(false);

    if (actionType === 'branch') {
      // Initialize branches for NEW branch node
      const fallbackBranch = createFallbackBranch();
      setBranches([fallbackBranch]); // Set 'branches' state first
      setIsBranchConfigOpen(true); // Then open sheet (useEffect will copy to tempBranches)
    } else {
      resetActionForm();
      // Important: Clear selectedActionNode if we chose from the modal
      // Otherwise, handleSaveActionConfig might think we are editing
      // This happens implicitly because selectedActionNode is only set onNodeClick
      // setSelectedActionNode(null); // Should not be needed if flow is correct
      setIsActionConfigSheetOpen(true);
    }
  };

  // Load configuration when a node is selected for editing
  useEffect(() => {
    if (selectedActionNode?.type === 'branch-config') {
      setActionSourceNode(selectedActionNode.id);
      // Load existing branches from the node's data
      setBranches(selectedActionNode.data?.branches || []);
      setIsBranchConfigOpen(true);
      return;
    }

    // --- Standard Action Editing ---
    if (selectedActionNode) {
      setCurrentActionType(selectedActionNode.data.actionType);

      // Restore action specific configuration
      if (selectedActionNode.data.actionType === 'send_message') {
        setMessageText(selectedActionNode.data.config?.message || '');
      } else if (selectedActionNode.data.actionType === 'reply_comment') {
        setCommentText(selectedActionNode.data.config?.comment || '');
      } else if (selectedActionNode.data.actionType === 'trigger_webhook') {
        setWebhookUrl(selectedActionNode.data.config?.url || '');
        setWebhookMethod(selectedActionNode.data.config?.method || 'POST');
      } else if (selectedActionNode.data.actionType === 'ai_select_message') {
        setAiMessages(selectedActionNode.data.config?.messages || []);
        setAiCustomPrompt(selectedActionNode.data.config?.custom_prompt || '');
        setTempAiMessageDoc(toTipTapDoc(''));
      } else if (
        selectedActionNode.data.actionType === 'linkedin_reply_comment'
      ) {
        setLinkedinActionComment(selectedActionNode.data.config?.comment || '');
      } else if (selectedActionNode.data.actionType === 'contact_add_tag') {
        setSelectedAddTags(selectedActionNode.data.config?.tags || []);
      } else if (selectedActionNode.data.actionType === 'contact_remove_tag') {
        setSelectedRemoveTags(selectedActionNode.data.config?.tags || []);
      } else if (
        selectedActionNode.data.actionType === 'contact_state_change'
      ) {
        setContactState(selectedActionNode.data.config?.state || 'open');
      } else if (
        selectedActionNode.data.actionType === 'contact_change_owner'
      ) {
        const config = selectedActionNode.data.config;
        if (config?.ownerMode === 'multiple' && Array.isArray(config?.owners)) {
          setOwnerMode('multiple');
          setOwnerAllocations(config.owners);
          setContactOwner('');
        } else {
          setOwnerMode('single');
          setContactOwner(config?.owner || '');
          setOwnerAllocations([]);
        }
      } else if (selectedActionNode.data.actionType === 'delay') {
        setDelayValue(selectedActionNode.data.config?.value || '');
        setDelayUnit(selectedActionNode.data.config?.unit || 'minutes');
      }
      setIsActionConfigSheetOpen(true);
    }
  }, [
    selectedActionNode,
    setCurrentActionType,
    setIsActionConfigSheetOpen,
    setActionSourceNode,
    setBranches,
    setIsBranchConfigOpen,
    setWebhookUrl,
    setWebhookMethod,
  ]);

  const resetActionForm = () => {
    setMessageText('');
    setCommentText('');
    setSelectedAddTags([]);
    setSelectedRemoveTags([]);
    setContactState('open');
    setContactOwner('');
    setOwnerMode('single');
    setOwnerAllocations([]);
    setDelayValue('');
    setDelayUnit('minutes');
    setWebhookUrl('');
    setWebhookMethod('POST');
    setAiMessages([]);
    setTempAiMessageDoc(toTipTapDoc(''));
    setAiCustomPrompt('');
  };

  // Initialize branch temp state when branch sheet opens
  useEffect(() => {
    if (isBranchConfigOpen) {
      setFieldOptions(staticFieldOptions);
      // Ensure fallback branch logic runs correctly using 'branches' state loaded from node
      let currentBranches = Array.isArray(branches) ? [...branches] : [];
      const fallbackIndex = currentBranches.findIndex((b) =>
        b.id?.startsWith('fallback-')
      );

      if (fallbackIndex === -1) {
        currentBranches.push(createFallbackBranch());
      } else if (fallbackIndex !== currentBranches.length - 1) {
        const fallback = currentBranches.splice(fallbackIndex, 1)[0];
        currentBranches.push(fallback);
      }
      currentBranches = currentBranches.map((branch) => ({
        ...branch,
        conditions: Array.isArray(branch.conditions)
          ? branch.conditions.map((group) =>
              Array.isArray(group) ? group : []
            )
          : [],
      }));
      setTempBranches(currentBranches);
    }
  }, [isBranchConfigOpen, branches, staticFieldOptions]);

  function parserMessageText(message) {
    if (typeof message === 'string') return message;

    // Parse the JSON structure
    const parsedText = message.content
      .map((node) => {
        if (node.type === 'paragraph') {
          const text = node.content
            ?.map((content) => {
              if (content.type === 'text') {
                return content.text;
              } else if (content.type === 'mention') {
                return `{${content.attrs.id}}`;
              } else if (content.type === 'hardBreak') {
                //console.log('Found hard break!');
                return '\n'; // Convert hard break to newline
              }
              return '';
            })
            .join('');

          return text;
        }
        return '';
      })
      .join('\n');

    return parsedText;
  }

  function constructMentionsEditorJson(text) {
    // Split the text by newlines to create paragraphs
    const paragraphs = text.split('\n');

    // Construct the JSON structure
    const json = {
      type: 'doc',
      content: paragraphs.map((paragraph) => {
        // Split the paragraph into segments (text and mentions)
        const segments = paragraph.split(/(\{[^}]+\})/);

        // Filter out empty segments and create content array
        const content = segments
          .map((segment) => {
            if (segment.startsWith('{') && segment.endsWith('}')) {
              // This is a mention
              const mentionId = segment.slice(1, -1);
              return {
                type: 'mention',
                attrs: {
                  id: mentionId,
                  label: mentionId,
                },
              };
            } else if (segment.trim() !== '') {
              // Only create text nodes for non-empty segments

              let trimmedSegment = segment;

              // Only trim if the segment is not just a single space (to preserve word separators)
              if (trimmedSegment !== ' ') {
                if (trimmedSegment.length > 0 && trimmedSegment[0] === ' ') {
                  trimmedSegment = trimmedSegment.slice(1);
                }
              }

              return {
                type: 'text',
                text: trimmedSegment,
              };
            }
            return null;
          })
          .filter(Boolean);

        // If content is empty, add a single space to prevent empty text node
        if (content.length === 0) {
          content.push({
            type: 'text',
            text: ' ',
          });
        }

        return {
          type: 'paragraph',
          content,
        };
      }),
    };

    return json;
  }

  // Add a new node to the flow
  const addNode = (type, label, data = {}) => {
    const sourceNode = nodes.find((node) => node.id === actionSourceNode);
    if (!sourceNode) return null;

    const targetEdge = edges.find((edge) => edge.source === actionSourceNode);
    const targetNodeId = targetEdge?.target;

    const newNodeId = `${type}-${uuidv4()}`;
    const newNode = {
      id: newNodeId,
      type: data.actionType ? 'action' : type, // Use 'action' for actions
      position: { x: 200, y: 200 }, // Initial position
      data: {
        label,
        ...data,
        // *** Attach the correct deletion handler based on type ***
        ...(type === 'action' && { handleActionDelete }),
      },
    };

    setNodes((nds) => [...nds, newNode]);

    // Update edges: Remove old edge, add source->new and new->target
    setEdges((eds) => {
      let newEdges = eds;
      if (targetEdge) {
        newEdges = eds.filter((edge) => edge.id !== targetEdge.id);
      }

      newEdges = [
        ...newEdges,
        {
          id: `e-${sourceNode.id}-${newNodeId}`,
          source: sourceNode.id,
          target: newNodeId,
          type: 'custom',
          className: 'stroke-slate-300 stroke-[1.5px]',
          data: {
            showAddButton: true,
            allowBranchStep: true,
            setIsActionTypeModalOpen,
            setActionSourceNode,
          },
        },
      ];

      // Edge from new node to original target (if exists)
      if (targetNodeId) {
        const targetNode = nodes.find((n) => n.id === targetNodeId);
        // Ensure target node still exists and wasn't part of a deletion triggered elsewhere
        if (targetNode) {
          newEdges = [
            ...newEdges,
            {
              id: `e-${newNodeId}-${targetNodeId}`,
              source: newNodeId,
              target: targetNodeId,
              type: 'custom',
              className: 'stroke-slate-300 stroke-[1.5px]',
              data: {
                showAddButton: true,
                allowBranchStep: false,
                setIsActionTypeModalOpen,
                setActionSourceNode,
              },
            },
          ];
        }
      }
      return newEdges;
    });

    setActionSourceNode(null); // Reset source node selection
    // runLayout(); // Layout is handled by effect in WorkflowEditor
    return newNodeId;
  };

  // Save Standard Action Configuration (Update or Create)
  const handleSaveActionConfig = () => {
    let isValid = false;
    let config = {};
    let actionOption = null;

    actionOption = [...actionOptions, ...otherOptions].find(
      (opt) => opt.type === currentActionType
    );
    if (!actionOption || currentActionType === 'branch') {
      console.error('Invalid action type for standard config saving.');
      return;
    }

    // --- Validation and Config Building (Keep as is, ensure tags/owners fetched) ---
    if (currentActionType === 'send_message') {
      isValid = messageText.trim() !== '';
      config = { message: messageText.trim() };
    } else if (currentActionType === 'reply_comment') {
      isValid = commentText.trim() !== '';
      config = { comment: commentText.trim() };
    } else if (currentActionType === 'trigger_webhook') {
      isValid = webhookUrl.trim() !== '';
      config = { url: webhookUrl.trim(), method: webhookMethod };
    } else if (currentActionType === 'ai_select_message') {
      isValid = aiMessages.length > 0;
      if (!isValid) {
        toast.error('Please add at least one message for AI to select from.');
        return;
      }
      config = {
        messages: aiMessages,
        custom_prompt: aiCustomPrompt.trim(),
      };
    } else if (currentActionType === 'linkedin_reply_comment') {
      isValid = linkedinActionComment.trim() !== '';
      config = { comment: linkedinActionComment.trim() };
    } else if (currentActionType === 'contact_add_tag') {
      isValid = selectedAddTags.length > 0;
      if (isValid) {
        const tagObjects = selectedAddTags
          .map((tagId) => {
            const tagObj = tags.find((t) => t._id === tagId);
            return tagObj
              ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
              : null;
          })
          .filter(Boolean);
        config = { tags: selectedAddTags, tagObjects };
      }
    } else if (currentActionType === 'contact_remove_tag') {
      isValid = selectedRemoveTags.length > 0;
      if (isValid) {
        const tagObjects = selectedRemoveTags
          .map((tagId) => {
            const tagObj = tags.find((t) => t._id === tagId);
            return tagObj
              ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
              : null;
          })
          .filter(Boolean);
        config = { tags: selectedRemoveTags, tagObjects };
      }
    } else if (currentActionType === 'contact_state_change') {
      isValid = ['open', 'close', 'spam'].includes(contactState);
      config = { state: contactState };
    } else if (currentActionType === 'contact_change_owner') {
      if (ownerMode === 'single') {
        isValid =
          contactOwner !== '' && owners.some((o) => o._id === contactOwner);
        if (isValid) {
          const ownerObj = owners.find((o) => o._id === contactOwner);
          config = {
            ownerMode: 'single',
            owner: contactOwner,
            ownerObject: ownerObj
              ? {
                  id: ownerObj._id,
                  name: `${ownerObj.first_name} ${ownerObj.last_name}`,
                }
              : null,
          };
        }
      } else {
        // Multiple owners with weighted allocation
        const validationResult =
          weightedAllocationSchema.safeParse(ownerAllocations);
        if (!validationResult.success) {
          const errorMessage =
            validationResult.error.errors[0]?.message ||
            'Invalid allocation configuration';
          toast.error(errorMessage);
          return;
        }
        isValid = true;
        // Build owner objects for display purposes
        const ownersWithDetails = ownerAllocations.map((alloc) => {
          const ownerObj = owners.find((o) => o._id === alloc.user_id);
          return {
            ...alloc,
            ownerObject: ownerObj
              ? {
                  id: ownerObj._id,
                  name: `${ownerObj.first_name} ${ownerObj.last_name}`,
                }
              : null,
          };
        });
        config = {
          ownerMode: 'multiple',
          owners: ownerAllocations,
          ownersWithDetails,
        };
      }
    } else if (currentActionType === 'delay') {
      const parsedValue = parseFloat(delayValue);
      isValid =
        delayValue !== '' &&
        !isNaN(parsedValue) &&
        parsedValue > 0 &&
        delayUnitOptions.some((u) => u.value === delayUnit);
      if (isValid) {
        config = { value: parsedValue, unit: delayUnit };
      } else {
        console.warn('Invalid delay configuration:', { delayValue, delayUnit });
      }
    }

    if (!isValid) {
      toast.error(
        'Action configuration is invalid. Please complete all fields.'
      );
      return;
    }

    // --- Update or Add Node ---
    if (selectedActionNode) {
      // --- Update Existing Node ---
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === selectedActionNode.id) {
            return {
              ...node,
              data: {
                ...node.data, // Keep position etc.
                label: actionOption.label,
                description: actionOption.description,
                bgClass: actionOption.bgClass,
                borderClass: actionOption.borderClass,
                iconClass: actionOption.iconClass,
                icon: actionOption.icon,
                actionType: currentActionType,
                config,
                handleActionDelete,
              },
            };
          }
          return node;
        })
      );
      toast.success(`Action '${actionOption.label}' updated.`);
    } else {
      // --- Create New Node ---
      addNode('action', actionOption.label, {
        description: actionOption.description,
        bgClass: actionOption.bgClass,
        borderClass: actionOption.borderClass,
        iconClass: actionOption.iconClass,
        icon: actionOption.icon,
        actionType: currentActionType,
        config,
        // `addNode` internally adds handleActionDelete
      });
      toast.success(`Action '${actionOption.label}' added.`);
    }

    // --- Reset and Close ---
    setIsActionConfigSheetOpen(false);
    setSelectedActionNode(null);
    setCurrentActionType(null);
    // runLayout(); // Handled by effect
  };

  // --- Branch Configuration Functions ---

  // Default fallback branch structure
  const createFallbackBranch = () => ({
    id: `fallback-${uuidv4()}`,
    name: 'Fallback',
    conditions: [],
  });

  const handleBranchNameChange = (branchIndex, newName) => {
    setTempBranches((prev) =>
      prev.map((branch, index) =>
        index === branchIndex ? { ...branch, name: newName } : branch
      )
    );
  };

  const handleDeleteBranch = (branchIndexToDelete) => {
    if (tempBranches[branchIndexToDelete]?.id?.startsWith('fallback-')) {
      toast.info('The Fallback branch cannot be deleted.');
      return;
    }
    const nonFallbackBranches = tempBranches.filter(
      (b) => !b.id?.startsWith('fallback-')
    );
    if (nonFallbackBranches.length <= 1) {
      toast.error('At least one branch is required besides Fallback.');
      return;
    }
    setTempBranches((prev) =>
      prev.filter((_, index) => index !== branchIndexToDelete)
    );
  };

  // Function to add a new AND condition to a specific group within a specific branch
  const handleAddAndCondition = (branchIndex, groupIndex) => {
    setTempBranches((prevBranches) => {
      const updatedBranches = [...prevBranches];
      const updatedConditions = [...updatedBranches[branchIndex].conditions];
      // Use the first field option as default when adding
      const defaultField = fieldOptions[0]?.value || '';
      const defaultFieldInfo = fieldOptions.find(
        (opt) => opt.value === defaultField
      );
      let defaultValue = '';
      let defaultOperator = '';
      if (defaultFieldInfo) {
        switch (defaultFieldInfo.type) {
          case 'owner-select':
          case 'tag-select':
            defaultOperator = 'equals';
            defaultValue = '';
            break;
          case 'status-select':
            defaultOperator = 'equals';
            defaultValue = 'open';
            break;
          case 'boolean':
            defaultOperator = 'equals';
            defaultValue = true;
            break;
          case 'number':
            defaultOperator = 'gt';
            defaultValue = '';
            break;
          case 'number-unit-days':
            defaultOperator = 'lt';
            defaultValue = '';
            break;
          case 'no-input':
            defaultOperator = 'boolean';
            defaultValue = true;
            break;
        }
      }

      updatedConditions[groupIndex] = [
        ...updatedConditions[groupIndex],
        { field: defaultField, operator: defaultOperator, value: defaultValue },
      ];
      updatedBranches[branchIndex] = {
        ...updatedBranches[branchIndex],
        conditions: updatedConditions,
      };
      return updatedBranches;
    });
  };

  // Function to add a new OR condition group to a specific branch
  const handleAddOrCondition = (branchIndex) => {
    setTempBranches((prevBranches) => {
      const updatedBranches = [...prevBranches];
      // Use the first field option as default when adding
      const defaultField = fieldOptions[0]?.value || '';
      const defaultFieldInfo = fieldOptions.find(
        (opt) => opt.value === defaultField
      );
      let defaultValue = '';
      let defaultOperator = '';
      if (defaultFieldInfo) {
        switch (defaultFieldInfo.type) {
          case 'owner-select':
          case 'tag-select':
            defaultOperator = 'equals';
            defaultValue = '';
            break;
          case 'status-select':
            defaultOperator = 'equals';
            defaultValue = 'open';
            break;
          case 'boolean':
            defaultOperator = 'equals';
            defaultValue = true;
            break;
          case 'number':
            defaultOperator = 'gt';
            defaultValue = '';
            break;
          case 'number-unit-days':
            defaultOperator = 'lt';
            defaultValue = '';
            break;
          case 'no-input':
            defaultOperator = 'boolean';
            defaultValue = true;
            break;
        }
      }
      const updatedConditions = [
        ...updatedBranches[branchIndex].conditions,
        [
          {
            field: defaultField,
            operator: defaultOperator,
            value: defaultValue,
          },
        ],
      ];
      updatedBranches[branchIndex] = {
        ...updatedBranches[branchIndex],
        conditions: updatedConditions,
      };
      return updatedBranches;
    });
  };

  // Function to add the first condition group if a branch has none
  const handleAddFirstCondition = (branchIndex) => {
    setTempBranches((prevBranches) => {
      const updatedBranches = [...prevBranches];
      // Use the first field option as default when adding
      const defaultField = fieldOptions[0]?.value || '';
      const defaultFieldInfo = fieldOptions.find(
        (opt) => opt.value === defaultField
      );
      let defaultValue = '';
      let defaultOperator = '';
      if (defaultFieldInfo) {
        switch (defaultFieldInfo.type) {
          case 'owner-select':
          case 'tag-select':
            defaultOperator = 'equals';
            defaultValue = '';
            break;
          case 'status-select':
            defaultOperator = 'equals';
            defaultValue = 'open';
            break;
          case 'boolean':
            defaultOperator = 'equals';
            defaultValue = true;
            break;
          case 'number':
            defaultOperator = 'gt';
            defaultValue = '';
            break;
          case 'number-unit-days':
            defaultOperator = 'lt';
            defaultValue = '';
            break;
          case 'no-input':
            defaultOperator = 'boolean';
            defaultValue = true;
            break;
        }
      }

      if (updatedBranches[branchIndex].conditions.length === 0) {
        updatedBranches[branchIndex].conditions = [
          [
            {
              field: defaultField,
              operator: defaultOperator,
              value: defaultValue,
            },
          ],
        ];
      }
      return updatedBranches;
    });
  };

  // Function to delete a specific condition
  const handleFilterDelete = (branchIndex, groupIndex, conditionIndex) => {
    setTempBranches((prevBranches) => {
      const updatedBranches = [...prevBranches];
      const branchConditions = [...updatedBranches[branchIndex].conditions];
      const groupConditions = [...branchConditions[groupIndex]];

      // Remove the specific condition
      groupConditions.splice(conditionIndex, 1);

      // If the group is now empty, remove the group
      if (groupConditions.length === 0) {
        branchConditions.splice(groupIndex, 1);
      } else {
        branchConditions[groupIndex] = groupConditions;
      }

      // If all groups are removed, reset conditions to an empty array for that branch
      if (branchConditions.length === 0) {
        updatedBranches[branchIndex].conditions = [];
      } else {
        updatedBranches[branchIndex].conditions = branchConditions;
      }

      return updatedBranches;
    });
  };

  // --- Branch Management Functions ---

  const handleAddBranch = () => {
    const newBranchId = uuidv4();
    // Use the first field option as default when adding
    const defaultField = fieldOptions[0]?.value || '';
    const defaultFieldInfo = fieldOptions.find(
      (opt) => opt.value === defaultField
    );
    let defaultValue = '';
    let defaultOperator = '';
    if (defaultFieldInfo) {
      switch (defaultFieldInfo.type) {
        case 'owner-select':
        case 'tag-select':
          defaultOperator = 'equals';
          defaultValue = '';
          break;
        case 'status-select':
          defaultOperator = 'equals';
          defaultValue = 'open';
          break;
        case 'boolean':
          defaultOperator = 'equals';
          defaultValue = true;
          break;
        case 'number':
          defaultOperator = 'gt';
          defaultValue = '';
          break;
        case 'number-unit-days':
          defaultOperator = 'lt';
          defaultValue = '';
          break;
        case 'no-input':
          defaultOperator = 'boolean';
          defaultValue = true;
          break;
      }
    }
    const newBranch = {
      id: newBranchId,
      name: `Branch ${tempBranches.length}`, // Default name
      conditions: [
        [
          {
            field: defaultField,
            operator: defaultOperator,
            value: defaultValue,
          },
        ],
      ], // Start with one default condition
    };
    setTempBranches((prevBranches) => {
      // Insert new branch before the fallback
      const fallbackIndex = prevBranches.findIndex((b) =>
        b.id.startsWith('fallback-')
      );
      if (fallbackIndex !== -1) {
        return [
          ...prevBranches.slice(0, fallbackIndex),
          newBranch,
          ...prevBranches.slice(fallbackIndex),
        ];
      }
      // Should not happen if fallback is always managed, but handle anyway
      return [...prevBranches, newBranch];
    });
  };

  // Function to validate branches and conditions before saving
  const validateBranches = () => {
    // Check if there are any non-fallback branches
    const nonFallbackBranches = tempBranches.filter(
      (b) => !b.id.startsWith('fallback-')
    );
    if (nonFallbackBranches.length === 0) {
      toast.error(
        'At least one branch definition is required besides the Fallback.'
      );
      return false;
    }

    let allValid = true;
    tempBranches.forEach((branch, index) => {
      // Check branch name (allow empty for fallback)
      if (!branch.id.startsWith('fallback-') && !branch.name.trim()) {
        toast.error(`Branch ${index + 1} needs a name.`);
        allValid = false;
        return; // Check next branch
      }

      // Fallback branch has no conditions to validate
      if (branch.id.startsWith('fallback-')) {
        return; // Continue to next branch
      }

      // Check if non-fallback branch has conditions defined
      if (
        !branch.conditions ||
        branch.conditions.length === 0 ||
        branch.conditions.every((group) => group.length === 0) // Check if all groups are empty
      ) {
        toast.error(
          `Branch &quot;${
            branch.name || 'Unnamed'
          }&quot; needs at least one condition.`
        );
        allValid = false;
        return; // Check next branch
      }

      // Check if all conditions within the branch are complete
      const branchIsValid = branch.conditions.every((group) =>
        group.every((condition) => {
          const fieldInfo = fieldOptions.find(
            (opt) => opt.value === condition.field
          );
          if (!fieldInfo) return false; // Invalid field selected

          // No operator/value needed for 'no-input' type
          if (fieldInfo.type === 'no-input') {
            return !!condition.field; // Just needs the field selected
          }

          // Check operator existence for other types
          if (!condition.operator) return false;

          // Check value based on type and operator
          switch (fieldInfo.type) {
            case 'owner-select':
            case 'tag-select':
              if (condition.operator === 'equals') {
                return condition.value !== undefined && condition.value !== '';
              } else if (condition.operator === 'in') {
                return (
                  Array.isArray(condition.value) && condition.value.length > 0
                );
              }
              return false; // Invalid operator for this type
            case 'status-select':
            case 'boolean':
              return condition.value !== undefined && condition.value !== '';
            case 'number':
              return (
                condition.value !== '' && !isNaN(parseFloat(condition.value))
              );
            case 'number-unit-days': {
              // Add block scope
              if (condition.operator === 'compare_within_timestamp') {
                // Validate the structured value object
                const valueObj = condition.value || {};
                return (
                  valueObj.operator &&
                  valueObj.unit &&
                  valueObj.unit_value !== undefined &&
                  valueObj.unit_value !== '' &&
                  !isNaN(parseFloat(valueObj.unit_value))
                );
              } else {
                // Standard validation for simple number values
                const numValue = parseFloat(condition.value);
                return condition.value !== '' && !isNaN(numValue);
              }
            } // Close block scope
            default:
              return false; // Unknown type
          }
        })
      );

      if (!branchIsValid) {
        toast.error(
          `Please complete all condition fields for branch &quot;${
            branch.name || 'Unnamed'
          }&quot;. Check fields, operators, and ensure values are entered correctly.`
        );
        allValid = false;
      }
    });

    return allValid;
  };

  // Handle save button click
  const handleSave = () => {
    if (validateBranches()) {
      // Pass the validated tempBranches structure back to the parent
      saveBranchConfig(tempBranches);
      setIsBranchConfigOpen(false); // Close sheet on successful save
    }
  };

  // Helper function to find all downstream elements
  // (Ensure this is defined or imported if needed for branch logic)
  const findAllDownstreamElements = useCallback(
    (startNodeId, allNodes, allEdges) => {
      const nodesToDelete = new Set();
      const edgesToDelete = new Set();
      const queue = [startNodeId];
      const visited = new Set(); // Prevent infinite loops in cycles

      while (queue.length > 0) {
        const currentNodeId = queue.shift();
        if (visited.has(currentNodeId)) continue;
        visited.add(currentNodeId);

        nodesToDelete.add(currentNodeId);

        const outgoingEdges = allEdges.filter(
          (e) => e.source === currentNodeId
        );
        for (const edge of outgoingEdges) {
          edgesToDelete.add(edge.id);
          const targetNode = allNodes.find((n) => n.id === edge.target);
          if (targetNode && !visited.has(targetNode.id)) {
            queue.push(targetNode.id);
          }
        }
      }
      return { nodesToDelete, edgesToDelete };
    },
    []
  );

  const saveBranchConfig = (updatedBranches) => {
    const currentNodes = nodes;
    const currentEdges = edges;

    // Validate the received branches
    if (!Array.isArray(updatedBranches) || updatedBranches.length === 0) {
      console.error('Invalid branches data received from configuration sheet.');
      alert('Error: Invalid branch data received.'); // User feedback
      return;
    }

    // Ensure there's always a fallback branch in the submitted data
    if (!updatedBranches.some((b) => b.id.startsWith('fallback-'))) {
      console.error('Branch configuration must include a fallback branch.');
      alert('Error: Fallback branch is missing.'); // User feedback
      return;
    }

    const branchConfigIcon =
      otherOptions.find((opt) => opt.type === 'branch')?.icon || Workflow;
    const branchConfigDetails =
      otherOptions.find((opt) => opt.type === 'branch') || {};

    // --- Editing Existing Branch ---
    if (selectedActionNode) {
      const configNode = currentNodes.find(
        (n) => n.id === selectedActionNode.id
      );

      if (!configNode || configNode.type !== 'branch-config') {
        console.error(
          'Editing branch: Config node not found or incorrect type.'
        );
        alert('Error: Could not find the branch configuration node to update.');
        return;
      }
      // 1. Map existing branch-data nodes connected FROM the config node
      const existingBranchDataMap = new Map(); // Map<branchId, {nodeId: string, targetId: string | null}>
      const edgesFromConfig = currentEdges.filter(
        (e) => e.source === selectedActionNode.id
      );
      edgesFromConfig.forEach((edgeToData) => {
        const dataNode = currentNodes.find(
          (n) => n.id === edgeToData.target && n.type === 'branch-data'
        );
        if (dataNode && dataNode.data?.branchId) {
          const edgeFromData = currentEdges.find(
            (e) => e.source === dataNode.id
          );
          existingBranchDataMap.set(dataNode.data.branchId, {
            nodeId: dataNode.id,
            targetId: edgeFromData ? edgeFromData.target : null,
            configEdgeId: edgeToData.id,
            dataEdgeId: edgeFromData ? edgeFromData.id : null,
          });
        }
      });
      const updatedBranchIds = new Set(updatedBranches.map((b) => b.id));
      let nodesToRemove = new Set();
      let edgesToRemove = new Set();
      // 2. Identify removed branches and collect elements for deletion
      for (const [
        branchId,
        { nodeId: branchDataNodeId, configEdgeId },
      ] of existingBranchDataMap.entries()) {
        if (!updatedBranchIds.has(branchId)) {
          // This branch was removed
          edgesToRemove.add(configEdgeId); // Edge: config -> data
          const {
            nodesToDelete: downstreamNodes,
            edgesToDelete: downstreamEdges,
          } = findAllDownstreamElements(
            branchDataNodeId,
            currentNodes,
            currentEdges
          );
          downstreamNodes.forEach((id) => nodesToRemove.add(id));
          downstreamEdges.forEach((id) => edgesToRemove.add(id));
        }
      }
      // 3. Prepare updates for remaining/updated branches and identify nodes/edges to keep/add
      const finalNodes = [];
      const finalEdges = [];
      const updatedNodeDataMap = new Map(); // Map<nodeId, newData>
      // Add the config node first, with updated branch data
      finalNodes.push({
        ...configNode,
        data: {
          ...configNode.data,
          branches: updatedBranches, // Store the latest branch structure
          handleDeleteBranchConfig,
          // Keep callbacks
          //   handleDelete: deleteNodeFn,
        },
      });
      // Process the branches from the sheet
      updatedBranches.forEach((branch, index) => {
        const existingBranchInfo = existingBranchDataMap.get(branch.id);
        if (existingBranchInfo) {
          // --- Branch Updated ---
          const {
            nodeId: branchDataNodeId,
            targetId: oldTargetId,
            configEdgeId,
            dataEdgeId,
          } = existingBranchInfo;
          // Check if this branch-data node itself or its target is marked for deletion (e.g., from another removed branch's path)
          if (nodesToRemove.has(branchDataNodeId)) {
            console.warn(
              `Branch data node ${branchDataNodeId} for updated branch ${branch.id} was marked for deletion. Skipping update.`
            );
            return; // Skip this branch as its root is gone
          }
          // Keep the existing branch-data node, just update its data
          const originalNode = currentNodes.find(
            (n) => n.id === branchDataNodeId
          );
          if (originalNode) {
            finalNodes.push({
              ...originalNode,
              data: {
                // Update data from the sheet
                name: branch.name,
                conditions: branch.conditions,
                branchId: branch.id,
              },
              // Position will be handled by layout effect
            });
            // Keep the edge from config -> data node
            const configEdge = currentEdges.find((e) => e.id === configEdgeId);
            if (configEdge) finalEdges.push(configEdge);
            // Handle the outgoing edge (data -> target)
            let currentTargetId = oldTargetId;
            let currentTargetNode = currentTargetId
              ? currentNodes.find((n) => n.id === currentTargetId)
              : null;
            // If the old target was deleted, create a new End node
            if (!currentTargetNode || nodesToRemove.has(currentTargetId)) {
              const newTargetId = `end-${branch.id}-${uuidv4().substring(
                0,
                4
              )}`;
              // Calculate position relative to the data node (layout effect will refine)
              const dataNodeX = originalNode.position.x;
              const dataNodeY = originalNode.position.y;
              finalNodes.push({
                id: newTargetId,
                type: 'end',
                position: { x: dataNodeX, y: dataNodeY + 150 },
                data: { label: 'END' },
              });
              // Add the new edge: data -> new End
              finalEdges.push({
                id: `e-${branchDataNodeId}-${newTargetId}`,
                source: branchDataNodeId,
                target: newTargetId,
                type: 'custom',
                className: 'stroke-slate-300 stroke-[1.5px]',
                data: {
                  //   onAddNode: onAddNodeClickFn,
                  //   sourceId: branchDataNodeId,
                  showAddButton: true,
                },
              });
              // Add the old data edge to removal set if it existed
              if (dataEdgeId) edgesToRemove.add(dataEdgeId);
            } else if (dataEdgeId) {
              // Old target exists and is not deleted, keep the existing edge
              const dataEdge = currentEdges.find((e) => e.id === dataEdgeId);
              if (dataEdge) finalEdges.push(dataEdge);
            }
          } else {
            console.warn(
              `Original branch data node ${branchDataNodeId} not found for update.`
            );
          }
        } else {
          // --- Branch Added (During Edit) ---
          // This logic is similar to the "Creating New Branch" case but targets this specific branch
          const configNodeX = configNode.position.x; // Use config node position as reference
          const configNodeY = configNode.position.y;
          // Simplified positioning for now, layout effect will fix
          const branchDataX =
            configNodeX + (index - (updatedBranches.length - 1) / 2) * 330; // Basic horizontal spread
          const branchDataY = configNodeY + 150;
          const targetY = branchDataY + 150;
          const branchDataId = `branch-data-${branch.id}-${uuidv4().substring(
            0,
            4
          )}`;
          finalNodes.push({
            id: branchDataId,
            type: 'branch-data',
            position: { x: branchDataX, y: branchDataY },
            data: {
              name: branch.name,
              conditions: branch.conditions,
              branchId: branch.id,
            },
          });
          // Edge: Config -> Data
          finalEdges.push({
            id: `e-${selectedActionNode.id}-${branchDataId}`,
            source: selectedActionNode.id,
            target: branchDataId,
            type: 'custom',
            className: 'stroke-slate-400 stroke-[1.5px]',
            data: { showAddButton: false },
          });
          // Create a new End node for the new branch path
          const targetId = `end-${branch.id}-${uuidv4().substring(0, 4)}`;
          finalNodes.push({
            id: targetId,
            type: 'end',
            position: { x: branchDataX, y: targetY },
            data: { label: 'END' },
          });
          // Edge: Data -> Target
          finalEdges.push({
            id: `e-${branchDataId}-${targetId}`,
            source: branchDataId,
            target: targetId,
            type: 'custom',
            className: 'stroke-slate-300 stroke-[1.5px]',
            data: {
              //   onAddNode: onAddNodeClickFn,
              //   sourceId: branchDataId,
              showAddButton: true,
              allowBranchStep: true,
              setIsActionTypeModalOpen,
              setActionSourceNode,
            },
          });
        }
      });
      // Add back any nodes/edges from the original state that weren't explicitly removed or processed above
      currentNodes.forEach((node) => {
        if (
          !finalNodes.some((n) => n.id === node.id) &&
          !nodesToRemove.has(node.id)
        ) {
          finalNodes.push(node);
        }
      });
      currentEdges.forEach((edge) => {
        if (
          !finalEdges.some((e) => e.id === edge.id) &&
          !edgesToRemove.has(edge.id)
        ) {
          finalEdges.push(edge);
        }
      });
      // 4. Apply Batch Update
      setNodes(finalNodes);
      setEdges(finalEdges.filter((e) => e != null)); // Filter out any potential nulls added
      // --- Creating New Branch ---
    } else {
      // This case creates the initial branch structure when adding a branch node
      // The existing logic seems okay, but let's ensure it uses batch updates too.
      const sourceNode = currentNodes.find((n) => n.id === actionSourceNode);
      if (!sourceNode) {
        console.error('Creating branch: Source node not found in state.');
        return;
      }

      const originalEdge = currentEdges.find(
        (e) => e.source === actionSourceNode
      );
      const originalTargetId = originalEdge?.target;

      let newNodes = [];
      let newEdges = [];
      let nodesToRemoveSet = new Set();
      let edgesToRemoveSet = new Set(originalEdge ? [originalEdge.id] : []);

      // Mark original End node for deletion if it exists and is connected to the source
      if (originalTargetId) {
        const originalTargetNode = currentNodes.find(
          (n) => n.id === originalTargetId
        );
        if (originalTargetNode && originalTargetNode.type === 'end') {
          nodesToRemoveSet.add(originalTargetId);
        }
      }

      // Create the branch-config node
      const branchConfigId = `branch-config-${uuidv4()}`;
      const configNodeY = sourceNode.position.y + 250;
      const configNodeX = sourceNode.position.x; // Center under source
      const configNode = {
        id: branchConfigId,
        type: 'branch-config',
        position: { x: configNodeX, y: configNodeY },
        data: {
          branchName: 'Branch Logic',
          branches: updatedBranches,
          icon: branchConfigIcon,
          bgClass: branchConfigDetails.bgClass,
          borderClass: branchConfigDetails.borderClass,
          iconClass: branchConfigDetails.iconClass,
          handleDeleteBranchConfig,
          //   onClick: handleActionNodeClickFn,
        },
      };
      newNodes.push(configNode);

      // Edge: Source -> Config
      newEdges.push({
        id: `e-${actionSourceNode}-${branchConfigId}`,
        source: actionSourceNode,
        target: branchConfigId,
        type: 'custom',
        className: 'stroke-slate-300 stroke-[1.5px]',
        data: {
          showAddButton: true,
          allowBranchStep: true,
          setIsActionTypeModalOpen,
          setActionSourceNode,
        },
      });

      // Create Branch Data Nodes and Edges
      const numBranches = updatedBranches.length;
      const totalWidth = numBranches * 280 + (numBranches - 1) * 50;
      const startX = configNodeX - totalWidth / 2 + 140; // Center alignment
      const branchDataY = configNodeY + 150;
      const targetY = branchDataY + 150;

      updatedBranches.forEach((branch, index) => {
        const branchDataId = `branch-data-${branch.id}-${uuidv4().substring(
          0,
          4
        )}`;
        const branchDataX = startX + index * (280 + 50);

        newNodes.push({
          id: branchDataId,
          type: 'branch-data',
          position: { x: branchDataX, y: branchDataY },
          data: {
            name: branch.name,
            conditions: branch.conditions,
            branchId: branch.id,
          },
        });

        // Edge: Config -> Data
        newEdges.push({
          id: `e-${branchConfigId}-${branchDataId}`,
          source: branchConfigId,
          target: branchDataId,
          type: 'custom',
          className: 'stroke-slate-400 stroke-[1.5px]',
          data: { showAddButton: false },
        });

        // Create a new End node for this path
        const targetId = `end-${branch.id}-${uuidv4().substring(0, 4)}`;
        newNodes.push({
          id: targetId,
          type: 'end',
          position: { x: branchDataX, y: targetY },
          data: { label: 'END' },
        });

        // Edge: Data -> Target
        newEdges.push({
          id: `e-${branchDataId}-${targetId}`,
          source: branchDataId,
          target: targetId,
          type: 'custom',
          className: 'stroke-slate-300 stroke-[1.5px]',
          data: {
            showAddButton: true,
            allowBranchStep: true,
            setIsActionTypeModalOpen,
            setActionSourceNode,
          },
        });
      });

      // Apply Updates using batch setters
      setNodes((prevNodes) => [
        ...prevNodes.filter((n) => !nodesToRemoveSet.has(n.id)),
        ...newNodes,
      ]);
      setEdges((prevEdges) => [
        ...prevEdges.filter((e) => !edgesToRemoveSet.has(e.id)),
        ...newEdges,
      ]);
      setActionSourceNode(null);
    }

    // Reset and close sheet
    setIsBranchConfigOpen(false);
    setSelectedActionNode(null);
    setCurrentActionType(null);
    setBranches([]);
  };

  // Handle sheet open/close changes
  const handleActionSheetOpenChange = (open) => {
    setIsActionConfigSheetOpen(open); // Update the open state
    if (!open) {
      // Reset state when the sheet closes, regardless of how
      resetActionForm();
      setSelectedActionNode(null);
      setCurrentActionType(null);
    }
  };

  // --- UI Rendering ---
  return (
    <>
      {/* Action Configuration Sheet */}
      <Sheet
        open={isActionConfigSheetOpen}
        onOpenChange={handleActionSheetOpenChange}
      >
        <SheetContent className='max-h-screen overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>
              {(
                actionOptions.find((opt) => opt.type === currentActionType) ||
                otherOptions.find((opt) => opt.type === currentActionType)
              )?.label || 'Configure Action'}
            </SheetTitle>
            <SheetDescription>
              Configure settings for this action step.
            </SheetDescription>
          </SheetHeader>

          {/* Form content based on currentActionType */}
          <div className='mt-6 space-y-6'>
            {/* Send Message */}
            {currentActionType === 'send_message' && (
              <div className='space-y-2'>
                <Label htmlFor='message'>Message Text</Label>
                <MentionsEditor
                  value={constructMentionsEditorJson(messageText)}
                  onChange={(value) => {
                    const parsed = parserMessageText(value);
                    setMessageText(parsed);
                  }}
                  returnType='json'
                  mentionSuggestions={['first_name']}
                />
              </div>
            )}

            {/* Reply Comment */}
            {currentActionType === 'reply_comment' && (
              <div className='space-y-2'>
                <Label htmlFor='comment'>Comment Reply Text</Label>
                <MentionsEditor
                  value={constructMentionsEditorJson(commentText)}
                  onChange={(value) => {
                    const parsed = parserMessageText(value);
                    setCommentText(parsed);
                  }}
                  returnType='json'
                  mentionSuggestions={['first_name']}
                />
              </div>
            )}

            {/* Add Tag */}
            {currentActionType === 'contact_add_tag' && (
              <div className='space-y-2'>
                <Label>Select Tags to Add</Label>
                <div className='rounded-md border'>
                  <div className='border-b bg-secondary/10 p-2 text-sm font-medium'>
                    Select tags
                  </div>
                  <ScrollArea className='h-[200px] p-2'>
                    {tags.map((tag) => {
                      const isSelected = selectedAddTags.includes(tag._id);
                      return (
                        <div
                          key={tag._id}
                          className='flex cursor-pointer items-center gap-2 rounded-md p-1.5 hover:bg-secondary/10'
                          onClick={() => {
                            if (isSelected) {
                              setSelectedAddTags(
                                selectedAddTags.filter((id) => id !== tag._id)
                              );
                            } else {
                              setSelectedAddTags([...selectedAddTags, tag._id]);
                            }
                          }}
                        >
                          <div
                            className={`flex h-4 w-4 items-center justify-center rounded border ${
                              isSelected
                                ? 'border-primary bg-primary'
                                : 'border-input'
                            }`}
                          >
                            {' '}
                            {isSelected && (
                              <Check className='h-3 w-3 text-primary-foreground' />
                            )}{' '}
                          </div>
                          <div className='flex items-center gap-2'>
                            {' '}
                            <div
                              className='h-2 w-2 rounded-full'
                              style={{
                                backgroundColor: tag.colour || '#6E56CF',
                              }}
                            />{' '}
                            <span className='text-sm'>{tag.name}</span>{' '}
                          </div>
                        </div>
                      );
                    })}
                  </ScrollArea>
                  {selectedAddTags.length > 0 && (
                    <div className='flex items-center justify-between border-t bg-secondary/10 p-2'>
                      {' '}
                      <span className='text-xs text-muted-foreground'>
                        {selectedAddTags.length} tag
                        {selectedAddTags.length !== 1 ? 's' : ''} selected
                      </span>{' '}
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 text-xs'
                        onClick={() => setSelectedAddTags([])}
                      >
                        {' '}
                        Clear all{' '}
                      </Button>{' '}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Remove Tag */}
            {currentActionType === 'contact_remove_tag' && (
              <div className='space-y-2'>
                <Label>Select Tags to Remove</Label>
                <div className='rounded-md border'>
                  <div className='border-b bg-secondary/10 p-2 text-sm font-medium'>
                    Select tags
                  </div>
                  <ScrollArea className='h-[200px] p-2'>
                    {tags.map((tag) => {
                      const isSelected = selectedRemoveTags.includes(tag._id);
                      return (
                        <div
                          key={tag._id}
                          className='flex cursor-pointer items-center gap-2 rounded-md p-1.5 hover:bg-secondary/10'
                          onClick={() => {
                            if (isSelected) {
                              setSelectedRemoveTags(
                                selectedRemoveTags.filter(
                                  (id) => id !== tag._id
                                )
                              );
                            } else {
                              setSelectedRemoveTags([
                                ...selectedRemoveTags,
                                tag._id,
                              ]);
                            }
                          }}
                        >
                          <div
                            className={`flex h-4 w-4 items-center justify-center rounded border ${
                              isSelected
                                ? 'border-primary bg-primary'
                                : 'border-input'
                            }`}
                          >
                            {' '}
                            {isSelected && (
                              <Check className='h-3 w-3 text-primary-foreground' />
                            )}{' '}
                          </div>
                          <div className='flex items-center gap-2'>
                            {' '}
                            <div
                              className='h-2 w-2 rounded-full'
                              style={{
                                backgroundColor: tag.colour || '#6E56CF',
                              }}
                            />{' '}
                            <span className='text-sm'>{tag.name}</span>{' '}
                          </div>
                        </div>
                      );
                    })}
                  </ScrollArea>
                  {selectedRemoveTags.length > 0 && (
                    <div className='flex items-center justify-between border-t bg-secondary/10 p-2'>
                      {' '}
                      <span className='text-xs text-muted-foreground'>
                        {selectedRemoveTags.length} tag
                        {selectedRemoveTags.length !== 1 ? 's' : ''} selected
                      </span>{' '}
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 text-xs'
                        onClick={() => setSelectedRemoveTags([])}
                      >
                        {' '}
                        Clear all{' '}
                      </Button>{' '}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Change State */}
            {currentActionType === 'contact_state_change' && (
              <div className='space-y-2'>
                <Label htmlFor='contactState'>Contact State</Label>
                <Select value={contactState} onValueChange={setContactState}>
                  <SelectTrigger id='contactState'>
                    <SelectValue placeholder='Select state' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='open'>Open</SelectItem>
                    <SelectItem value='close'>Archive</SelectItem>
                    <SelectItem value='spam'>Spam</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Change Owner */}
            {currentActionType === 'contact_change_owner' && (
              <div className='space-y-4'>
                {/* Owner Mode Selection */}
                <div className='space-y-2'>
                  <Label>Assignment Mode</Label>
                  <Select
                    value={ownerMode}
                    onValueChange={(value) => {
                      setOwnerMode(value);
                      if (value === 'single') {
                        setOwnerAllocations([]);
                      } else {
                        setContactOwner('');
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select mode' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='single'>Single Owner</SelectItem>
                      <SelectItem value='multiple'>
                        Multiple Owners (Weighted)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Single Owner Selection */}
                {ownerMode === 'single' && (
                  <div className='space-y-2'>
                    <Label htmlFor='contactOwner'>Contact Owner</Label>
                    <Select
                      value={contactOwner}
                      onValueChange={setContactOwner}
                    >
                      <SelectTrigger id='contactOwner'>
                        <SelectValue placeholder='Select owner' />
                      </SelectTrigger>
                      <SelectContent>
                        {owners.map((owner) => (
                          <SelectItem key={owner._id} value={owner._id}>
                            {owner.first_name} {owner.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Multiple Owners with Allocation */}
                {ownerMode === 'multiple' && (
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <div className='flex items-center justify-between'>
                        <Label>Owner Allocations</Label>
                        <span
                          className={`text-sm ${
                            ownerAllocations.reduce(
                              (sum, o) => sum + (o.allocation || 0),
                              0
                            ) === 100
                              ? 'text-green-600'
                              : 'text-destructive'
                          }`}
                        >
                          Total:{' '}
                          {ownerAllocations.reduce(
                            (sum, o) => sum + (o.allocation || 0),
                            0
                          )}
                          %
                        </span>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        Allocations must total 100%. Contacts will be assigned
                        randomly based on these weights.
                      </p>
                    </div>

                    {/* Owner Allocation List */}
                    <div className='space-y-3'>
                      {ownerAllocations.map((alloc, index) => (
                        <div
                          key={index}
                          className='flex items-center gap-2 rounded-md border p-3'
                        >
                          <div className='flex-1'>
                            <Select
                              value={alloc.user_id}
                              onValueChange={(value) => {
                                setOwnerAllocations((prev) =>
                                  prev.map((a, i) =>
                                    i === index ? { ...a, user_id: value } : a
                                  )
                                );
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder='Select owner' />
                              </SelectTrigger>
                              <SelectContent>
                                {owners.map((owner) => (
                                  <SelectItem
                                    key={owner._id}
                                    value={owner._id}
                                    disabled={ownerAllocations.some(
                                      (a, i) =>
                                        i !== index && a.user_id === owner._id
                                    )}
                                  >
                                    {owner.first_name} {owner.last_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className='w-24'>
                            <div className='relative'>
                              <Input
                                type='number'
                                min='1'
                                max='100'
                                value={alloc.allocation || ''}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  setOwnerAllocations((prev) =>
                                    prev.map((a, i) =>
                                      i === index
                                        ? { ...a, allocation: value }
                                        : a
                                    )
                                  );
                                }}
                                placeholder='%'
                                className='pr-6'
                              />
                              <span className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground'>
                                %
                              </span>
                            </div>
                          </div>
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            className='h-8 w-8 text-muted-foreground hover:text-destructive'
                            onClick={() => {
                              setOwnerAllocations((prev) =>
                                prev.filter((_, i) => i !== index)
                              );
                            }}
                          >
                            <TrashIcon className='h-4 w-4' />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Add Owner Button */}
                    <Button
                      type='button'
                      variant='outline'
                      className='w-full'
                      onClick={() => {
                        // Find first available owner not already selected
                        const availableOwner = owners.find(
                          (o) =>
                            !ownerAllocations.some((a) => a.user_id === o._id)
                        );
                        if (availableOwner) {
                          setOwnerAllocations((prev) => [
                            ...prev,
                            { user_id: availableOwner._id, allocation: 0 },
                          ]);
                        } else {
                          toast.error('All owners have been added');
                        }
                      }}
                      disabled={ownerAllocations.length >= owners.length}
                    >
                      <PlusCircledIcon className='mr-2 h-4 w-4' />
                      Add Owner
                    </Button>

                    {/* Quick allocation buttons */}
                    {ownerAllocations.length > 1 && (
                      <Button
                        type='button'
                        variant='secondary'
                        size='sm'
                        className='w-full'
                        onClick={() => {
                          const count = ownerAllocations.length;
                          const baseAllocation = Math.floor(100 / count);
                          const remainder = 100 % count;
                          setOwnerAllocations((prev) =>
                            prev.map((a, i) => ({
                              ...a,
                              allocation:
                                baseAllocation + (i < remainder ? 1 : 0),
                            }))
                          );
                        }}
                      >
                        Distribute Equally
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* LinkedIn Comment Configuration */}
            {currentActionType === 'linkedin_reply_comment' && (
              <div className='space-y-2'>
                <Label htmlFor='linkedin-comment'>Comment Reply Text</Label>
                <Textarea
                  id='linkedin-comment'
                  value={linkedinActionComment}
                  onChange={(e) => setLinkedinActionComment(e.target.value)}
                  placeholder='Enter the comment reply text...'
                  rows={4}
                />
                <p className='text-xs text-muted-foreground'>
                  This text will be posted as a reply to the comment on your
                  LinkedIn post.
                </p>
              </div>
            )}

            {/* Delay */}
            {currentActionType === 'delay' && (
              <div className='space-y-4'>
                <div className='flex items-center gap-2'>
                  <Clock className='h-4 w-4 text-muted-foreground' />
                  <Label>Wait for a specified period</Label>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <Label htmlFor='delay-value'>Delay Amount</Label>
                    <Input
                      id='delay-value'
                      type='number'
                      min='1'
                      value={delayValue}
                      onChange={(e) => setDelayValue(e.target.value)}
                      placeholder='Enter number'
                    />
                  </div>
                  <div>
                    <Label htmlFor='delay-unit'>Unit</Label>
                    <Select value={delayUnit} onValueChange={setDelayUnit}>
                      <SelectTrigger id='delay-unit'>
                        <SelectValue placeholder='Select unit' />
                      </SelectTrigger>
                      <SelectContent>
                        {delayUnitOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Webhook Configuration */}
            {currentActionType === 'trigger_webhook' && (
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='webhook-url'>Webhook URL</Label>
                  <Input
                    id='webhook-url'
                    type='url'
                    value={webhookUrl || ''}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder='https://your-webhook-url.com'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='webhook-method'>HTTP Method</Label>
                  <Select
                    value={webhookMethod || 'POST'}
                    onValueChange={setWebhookMethod}
                  >
                    <SelectTrigger id='webhook-method'>
                      <SelectValue placeholder='Select method' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='GET'>GET</SelectItem>
                      <SelectItem value='POST'>POST</SelectItem>
                      <SelectItem value='PUT'>PUT</SelectItem>
                      <SelectItem value='PATCH'>PATCH</SelectItem>
                      <SelectItem value='DELETE'>DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* AI Select Message Configuration */}
            {currentActionType === 'ai_select_message' && (
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>
                    Write your message you&apos;d like to send and click the +
                    button
                  </Label>
                  <div className='flex gap-2'>
                    <div className='flex-1'>
                      <MentionsEditor
                        value={tempAiMessageDoc}
                        onChange={setTempAiMessageDoc}
                        returnType='json'
                        mentionSuggestions={['first-name']}
                        placeholder='Your message goes here'
                      />
                    </div>
                    <Button
                      type='button'
                      onClick={() => {
                        const messageText =
                          fromTipTapDoc(tempAiMessageDoc).trim();
                        if (messageText) {
                          setAiMessages([...aiMessages, messageText]);
                          setTempAiMessageDoc(toTipTapDoc(''));
                        }
                      }}
                      variant='outline'
                      size='sm'
                    >
                      <PlusCircledIcon className='h-4 w-4' />
                    </Button>
                  </div>
                  {aiMessages.length > 0 && (
                    <div className='mt-2 space-y-2'>
                      {aiMessages.map((message, index) => (
                        <div
                          key={index}
                          className='flex items-center justify-between rounded-md border border-purple-500/20 bg-purple-500/10 px-3 py-2'
                        >
                          <div className='whitespace-pre-wrap text-sm'>
                            {convertToDisplayFormat(message)}
                          </div>
                          <Button
                            type='button'
                            onClick={() =>
                              setAiMessages(
                                aiMessages.filter((_, i) => i !== index)
                              )
                            }
                            variant='ghost'
                            size='sm'
                            className='h-6 w-6 p-0'
                          >
                            <TrashIcon className='h-3 w-3' />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='ai-custom-prompt'>
                    Custom Prompt (Optional)
                  </Label>
                  <Textarea
                    id='ai-custom-prompt'
                    value={aiCustomPrompt}
                    onChange={(e) => setAiCustomPrompt(e.target.value)}
                    placeholder='Enter a custom prompt'
                    rows={3}
                  />
                  <p className='text-xs text-muted-foreground'>
                    You can add in your own custom prompt if necessary. Leave
                    empty if you want Revio to take care of the prompt.
                  </p>
                </div>
              </div>
            )}
          </div>

          <SheetFooter className='mt-6 border-t pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                handleActionSheetOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type='button' onClick={handleSaveActionConfig}>
              {selectedActionNode ? 'Update Action' : 'Add Action'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Action Selection Modal */}
      <Dialog
        open={isActionTypeModalOpen}
        onOpenChange={setIsActionTypeModalOpen}
      >
        <DialogContent className='max-h-[95vh] sm:max-w-2xl'>
          <DialogHeader>
            {' '}
            <DialogTitle>Add a Step</DialogTitle>
          </DialogHeader>
          <div className='grid max-h-[80vh] grid-cols-1 gap-x-12 gap-y-4 py-4 md:grid-cols-2'>
            {/* Left: Actions */}
            <ScrollArea className='h-[70vh]' type='always'>
              <div className='flex flex-col gap-4 pr-4'>
                {actionOptions.map((option) => {
                  const channels =
                    channelCapabilities.actions[option.type] || [];
                  return (
                    <Button
                      key={option.type}
                      onClick={() => selectActionType(option.type)}
                      variant='outline'
                      className='flex h-auto w-full flex-col items-start justify-start gap-2 whitespace-normal p-4 hover:bg-accent/20'
                    >
                      <div className='flex w-full items-center gap-2'>
                        <option.icon
                          className={`h-5 w-5 ${option.iconClass}`}
                        />
                        <span className='font-medium'>{option.label}</span>
                      </div>
                      <span className='text-left text-xs text-muted-foreground'>
                        {option.description}
                      </span>
                      <div className='flex flex-wrap gap-1'>
                        {channels.map((ch) => {
                          const info = getChannelInfo(ch);
                          const IconComponent = info.icon;
                          return (
                            <Badge
                              key={ch}
                              variant='secondary'
                              className='flex items-center px-1.5 py-0.5'
                            >
                              {IconComponent && (
                                <IconComponent
                                  className={`h-3 w-3 ${info.color}`}
                                />
                              )}
                            </Badge>
                          );
                        })}
                      </div>
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
            {/* Right: Others (Delay, Branch) */}
            <ScrollArea className='h-[70vh]' type='always'>
              <div className='flex flex-col gap-4 pr-4'>
                {filteredOtherOptions.map((option) => {
                  const channels =
                    channelCapabilities.actions[option.type] || [];
                  return (
                    <Button
                      key={option.type}
                      onClick={() => selectActionType(option.type)}
                      variant='outline'
                      className='flex h-auto w-full flex-col items-start justify-start gap-2 whitespace-normal p-4 hover:bg-accent/20'
                    >
                      <div className='flex w-full items-center gap-2'>
                        <option.icon
                          className={`h-5 w-5 ${option.iconClass}`}
                        />
                        <span className='font-medium'>{option.label}</span>
                      </div>
                      <span className='text-left text-xs text-muted-foreground'>
                        {option.description}
                      </span>
                      {channels.length > 0 && (
                        <div className='flex flex-wrap gap-1'>
                          {channels.map((ch) => {
                            const info = getChannelInfo(ch);
                            const IconComponent = info.icon;
                            return (
                              <Badge
                                key={ch}
                                variant='secondary'
                                className='flex items-center px-1.5 py-0.5'
                              >
                                {IconComponent && (
                                  <IconComponent
                                    className={`h-3 w-3 ${info.color}`}
                                  />
                                )}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Branch Configuration Sheet */}
      <Sheet open={isBranchConfigOpen} onOpenChange={setIsBranchConfigOpen}>
        <SheetContent
          className='flex min-w-[500px] flex-col sm:min-w-[600px] lg:min-w-[700px]'
          side='right'
        >
          <SheetHeader>
            {' '}
            <SheetTitle>Configure Branches</SheetTitle>{' '}
          </SheetHeader>
          <ScrollArea className='flex-grow pr-4'>
            <div className='space-y-6 py-4'>
              {tempBranches
                .filter((branch) => !branch.id?.startsWith('fallback-'))
                .map((branch, mapIndex) => {
                  const originalIndex = tempBranches.findIndex(
                    (b) => b.id === branch.id
                  );
                  return (
                    <Card
                      key={branch.id || originalIndex}
                      className={'border-primary/20 shadow-sm'}
                    >
                      <CardContent className='p-4'>
                        <div className='mb-4 flex items-center justify-between'>
                          <Input
                            placeholder={`Branch ${mapIndex + 1} Name`}
                            value={branch.name || ''}
                            onChange={(e) =>
                              handleBranchNameChange(
                                originalIndex,
                                e.target.value
                              )
                            }
                            className={'text-lg font-semibold'}
                          />
                          <Button
                            variant='ghost'
                            size='icon'
                            className='ml-2 h-8 w-8 text-muted-foreground hover:text-destructive'
                            onClick={() => handleDeleteBranch(originalIndex)}
                            title='Delete branch'
                          >
                            {' '}
                            <TrashIcon className='h-4 w-4' />{' '}
                          </Button>
                        </div>
                        <label className='mb-2 block text-sm font-medium'>
                          Conditions (OR Groups):
                        </label>
                        {!branch.conditions ||
                        branch.conditions.length === 0 ||
                        branch.conditions.every((g) => g.length === 0) ? (
                          <Button
                            onClick={() =>
                              handleAddFirstCondition(originalIndex)
                            }
                            className='w-full'
                          >
                            {' '}
                            <PlusCircledIcon className='mr-2 h-4 w-4' /> Add
                            First Condition{' '}
                          </Button>
                        ) : (
                          <div className='space-y-4'>
                            {branch.conditions.map(
                              (group, groupIndex) =>
                                group.length > 0 && (
                                  <div
                                    key={`${branch.id}-group-${groupIndex}`}
                                    className='relative rounded-md border border-slate-200 p-3'
                                  >
                                    {groupIndex > 0 && (
                                      <div className='mb-3 flex items-center'>
                                        <div className='flex-grow border-t border-border'></div>
                                        <Badge
                                          variant='outline'
                                          className='mx-2'
                                        >
                                          OR
                                        </Badge>
                                        <div className='flex-grow border-t border-border'></div>
                                      </div>
                                    )}
                                    {group.map((condition, conditionIndex) => (
                                      <div
                                        key={`${branch.id}-cond-${groupIndex}-${conditionIndex}`}
                                      >
                                        <BranchFilterCard
                                          tempBranches={tempBranches}
                                          setTempBranches={setTempBranches}
                                          branchIndex={originalIndex}
                                          groupIndex={groupIndex}
                                          conditionIndex={conditionIndex}
                                          handleFilterDelete={
                                            handleFilterDelete
                                          }
                                        />
                                        {conditionIndex < group.length - 1 && (
                                          <div className='mb-3 flex items-center'>
                                            <div className='flex-grow border-t border-border'></div>
                                            <Badge
                                              variant='outline'
                                              className='mx-2'
                                            >
                                              AND
                                            </Badge>
                                            <div className='flex-grow border-t border-border'></div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    <Button
                                      variant='outline'
                                      size='sm'
                                      onClick={() =>
                                        handleAddAndCondition(
                                          originalIndex,
                                          groupIndex
                                        )
                                      }
                                      className='mt-2 w-full'
                                    >
                                      {' '}
                                      <PlusCircledIcon className='mr-2 h-4 w-4' />{' '}
                                      Add AND Condition{' '}
                                    </Button>
                                  </div>
                                )
                            )}
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() =>
                                handleAddOrCondition(originalIndex)
                              }
                              className='w-full'
                            >
                              {' '}
                              <PlusCircledIcon className='mr-2 h-4 w-4' /> Add
                              OR Group{' '}
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              <Button
                variant='outline'
                onClick={handleAddBranch}
                className='w-full border-dashed'
              >
                {' '}
                <PlusCircledIcon className='mr-2 h-4 w-4' /> New Branch
              </Button>
            </div>
          </ScrollArea>
          <SheetFooter className='mt-auto border-t pt-4'>
            <SheetClose asChild>
              <Button variant='outline'>Cancel</Button>
            </SheetClose>
            <Button onClick={handleSave}>Save Branches</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ActionConfiguration;

// --- Sub Components (OwnerSelector, TagSelector, BranchFilterCard - Keep as is, ensure imports correct) ---

const OwnerSelector = ({ selectedOwners, setSelectedOwners }) => {
  const owners = useInboxStore((state) =>
    (state.owners || []).filter(
      (owner) => owner.role !== 'deleted' && owner.role !== 'system'
    )
  );
  return (
    <div className='rounded-md border'>
      <div className='border-b bg-secondary/10 p-2 text-sm font-medium'>
        {' '}
        Select owners{' '}
      </div>
      <ScrollArea className='h-[200px] p-2'>
        {owners.map((owner) => {
          const isSelected = selectedOwners.includes(owner._id);
          return (
            <div
              key={owner._id}
              className='flex cursor-pointer items-center gap-2 rounded-md p-1.5 hover:bg-secondary/10'
              onClick={() => {
                if (isSelected) {
                  setSelectedOwners(
                    selectedOwners.filter((id) => id !== owner._id)
                  );
                } else {
                  setSelectedOwners([...selectedOwners, owner._id]);
                }
              }}
            >
              <div
                className={`flex h-4 w-4 items-center justify-center rounded border ${
                  isSelected ? 'border-primary bg-primary' : 'border-input'
                }`}
              >
                {' '}
                {isSelected && (
                  <Check className='h-3 w-3 text-primary-foreground' />
                )}{' '}
              </div>
              <span className='text-sm'>
                {' '}
                {owner.first_name} {owner.last_name}{' '}
              </span>
            </div>
          );
        })}
      </ScrollArea>
      {selectedOwners.length > 0 && (
        <div className='flex items-center justify-between border-t bg-secondary/10 p-2'>
          {' '}
          <span className='text-xs text-muted-foreground'>
            {selectedOwners.length} owner
            {selectedOwners.length !== 1 ? 's' : ''} selected
          </span>{' '}
          <Button
            variant='ghost'
            size='sm'
            className='h-7 text-xs'
            onClick={() => setSelectedOwners([])}
          >
            {' '}
            Clear all{' '}
          </Button>{' '}
        </div>
      )}
    </div>
  );
};

const TagSelector = ({ selectedTags, setSelectedTags }) => {
  const tags = useInboxStore(
    useShallow((state) =>
      state.tags.filter(
        (tag) =>
          !['No-Tag-SBCCRM', 'Seen-Not-Responded', 'Not-Responded'].includes(
            tag._id
          )
      )
    )
  );
  return (
    <div className='rounded-md border'>
      <div className='border-b bg-secondary/10 p-2 text-sm font-medium'>
        {' '}
        Select tags{' '}
      </div>
      <ScrollArea className='h-[200px] p-2'>
        {tags.map((tag) => {
          const isSelected = selectedTags.includes(tag._id);
          return (
            <div
              key={tag._id}
              className='flex cursor-pointer items-center gap-2 rounded-md p-1.5 hover:bg-secondary/10'
              onClick={() => {
                if (isSelected) {
                  setSelectedTags(selectedTags.filter((id) => id !== tag._id));
                } else {
                  setSelectedTags([...selectedTags, tag._id]);
                }
              }}
            >
              <div
                className={`flex h-4 w-4 items-center justify-center rounded border ${
                  isSelected ? 'border-primary bg-primary' : 'border-input'
                }`}
              >
                {' '}
                {isSelected && (
                  <Check className='h-3 w-3 text-primary-foreground' />
                )}{' '}
              </div>
              <div className='flex items-center gap-2'>
                {' '}
                <div
                  className='h-2 w-2 rounded-full'
                  style={{ backgroundColor: tag.colour || '#6E56CF' }}
                />{' '}
                <span className='text-sm'>{tag.name}</span>{' '}
              </div>
            </div>
          );
        })}
      </ScrollArea>
      {selectedTags.length > 0 && (
        <div className='flex items-center justify-between border-t bg-secondary/10 p-2'>
          {' '}
          <span className='text-xs text-muted-foreground'>
            {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''}{' '}
            selected
          </span>{' '}
          <Button
            variant='ghost'
            size='sm'
            className='h-7 text-xs'
            onClick={() => setSelectedTags([])}
          >
            {' '}
            Clear all{' '}
          </Button>{' '}
        </div>
      )}
    </div>
  );
};

const BranchFilterCard = ({
  tempBranches,
  setTempBranches,
  branchIndex,
  groupIndex,
  conditionIndex,
  handleFilterDelete,
}) => {
  const condition =
    tempBranches[branchIndex]?.conditions?.[groupIndex]?.[conditionIndex];
  const fieldOptions = useMemo(
    () => [
      {
        value: 'contact.user_id',
        label: 'Contact Owner',
        type: 'owner-select',
      },
      {
        value: 'contact.status',
        label: 'Contact Status',
        type: 'status-select',
      },
      { value: 'contact.tag_ids', label: 'Contact Tags', type: 'tag-select' },
      { value: 'contact.verified', label: 'Contact Verified', type: 'boolean' },
      {
        value: 'contact.followers_count',
        label: 'Follower Count',
        type: 'number',
      },
      {
        value: 'contact.following_count',
        label: 'Following Count',
        type: 'number',
      },
      {
        value: 'contact_messages._total_msgs_received',
        label: 'Total Msgs Received',
        type: 'number',
      },
      {
        value: 'contact_messages._prev_message_received_timestamp',
        label: 'Prev Msg Received Time',
        type: 'number-unit-days',
      },
      {
        value: 'contact_messages._none_messages_received_within',
        label: 'No Msgs Received Since Start',
        type: 'no-input',
      },
    ],
    []
  );

  if (!condition) {
    console.error('BranchFilterCard: Invalid condition access', {
      branchIndex,
      groupIndex,
      conditionIndex,
    });
    return <div className='text-red-500'>Error loading condition</div>;
  }

  const handleFieldChange = (field) => {
    const fieldInfo = fieldOptions.find((option) => option.value === field);
    let defaultValue = '';
    let defaultOperator = '';
    if (fieldInfo) {
      switch (fieldInfo.type) {
        case 'owner-select':
        case 'tag-select':
          defaultOperator = 'equals';
          defaultValue = '';
          break;
        case 'status-select':
          defaultOperator = 'equals';
          defaultValue = 'open';
          break;
        case 'boolean':
          defaultOperator = 'equals';
          defaultValue = true;
          break;
        case 'number':
          defaultOperator = 'gt';
          defaultValue = '';
          break;
        case 'number-unit-days':
          if (field === 'contact_messages._prev_message_received_timestamp') {
            defaultOperator = 'compare_within_timestamp';
            defaultValue = { operator: 'gt', unit: 'days', unit_value: '' };
          } else {
            defaultOperator = 'lt';
            defaultValue = '';
          }
          break;
        case 'no-input':
          defaultOperator = 'boolean';
          defaultValue = true;
          break;
      }
    }
    setTempBranches((prev) => {
      const updated = [...prev];
      updated[branchIndex].conditions[groupIndex][conditionIndex] = {
        ...updated[branchIndex].conditions[groupIndex][conditionIndex],
        field,
        value: defaultValue,
        operator: defaultOperator,
      };
      return updated;
    });
  };

  const updateValue = (value) => {
    // Get the field info to determine the field type
    const fieldInfo = fieldOptions.find(
      (option) => option.value === condition.field
    );

    // Convert value to proper type based on field type
    let typedValue = value;
    if (fieldInfo && fieldInfo.type === 'boolean') {
      // Convert 'true'/'false' strings to actual boolean values
      typedValue = value === 'true';
    }

    setTempBranches((prev) => {
      const updated = [...prev];
      updated[branchIndex].conditions[groupIndex][conditionIndex] = {
        ...updated[branchIndex].conditions[groupIndex][conditionIndex],
        value: typedValue,
      };
      return updated;
    });
  };

  const updateOperator = (operator) => {
    setTempBranches((prev) => {
      const updated = [...prev];
      const currentCondition =
        updated[branchIndex].conditions[groupIndex][conditionIndex];
      const fieldInfo = fieldOptions.find(
        (opt) => opt.value === currentCondition.field
      );
      let currentValue = currentCondition.value;

      if (
        fieldInfo?.type === 'owner-select' ||
        fieldInfo?.type === 'tag-select'
      ) {
        if (operator === 'equals' && Array.isArray(currentValue))
          currentValue = '';
        else if (operator === 'in' && !Array.isArray(currentValue))
          currentValue = [];
      } else if (fieldInfo?.type === 'number-unit-days') {
        if (operator === 'compare_within_timestamp') {
          currentValue = {
            operator: 'gt',
            unit: 'days',
            unit_value: typeof currentValue === 'string' ? currentValue : '',
          };
        } else if (typeof currentValue === 'object') {
          currentValue = currentValue.unit_value || '';
        }
      }

      updated[branchIndex].conditions[groupIndex][conditionIndex] = {
        ...currentCondition,
        operator,
        value: currentValue,
      };
      return updated;
    });
  };

  const renderValueInput = () => {
    const inputFieldInfo = fieldOptions.find(
      (opt) => opt.value === condition.field
    );
    if (!inputFieldInfo || inputFieldInfo.type === 'no-input') return null;

    switch (inputFieldInfo.type) {
      case 'owner-select':
        if (condition.operator === 'equals') {
          return (
            <Select value={condition.value || ''} onValueChange={updateValue}>
              <SelectTrigger>
                <SelectValue placeholder='Select owner' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Owners</SelectLabel>
                  {useInboxStore
                    .getState()
                    .owners?.filter(
                      (owner) =>
                        owner.role !== 'deleted' && owner.role !== 'system'
                    )
                    .map((o) => (
                      <SelectItem key={o._id} value={o._id}>
                        {o.first_name} {o.last_name}
                      </SelectItem>
                    ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          );
        } else if (condition.operator === 'in') {
          return (
            <OwnerSelector
              selectedOwners={condition.value || []}
              setSelectedOwners={updateValue}
            />
          );
        }
        break;
      case 'tag-select':
        if (condition.operator === 'equals') {
          const tags = useInboxStore
            .getState()
            .tags.filter(
              (tag) =>
                ![
                  'No-Tag-SBCCRM',
                  'Seen-Not-Responded',
                  'Not-Responded',
                ].includes(tag._id)
            );
          return (
            <Select value={condition.value || ''} onValueChange={updateValue}>
              <SelectTrigger>
                <SelectValue placeholder='Select tag' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Tags</SelectLabel>
                  {tags.map((tag) => (
                    <SelectItem key={tag._id} value={tag._id}>
                      <div className='flex items-center'>
                        <span
                          className='mr-2 inline-block h-2 w-2 rounded-full'
                          style={{ backgroundColor: tag.colour }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          );
        } else if (condition.operator === 'in') {
          return (
            <TagSelector
              selectedTags={condition.value || []}
              setSelectedTags={updateValue}
            />
          );
        }
        break;
      case 'status-select':
        return (
          <Select value={condition.value || 'open'} onValueChange={updateValue}>
            <SelectTrigger>
              <SelectValue placeholder='Select status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='open'>Open</SelectItem>
              <SelectItem value='close'>Archive</SelectItem>
              <SelectItem value='spam'>Spam</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'boolean':
        return (
          <Select
            value={String(condition.value ?? 'true')}
            onValueChange={updateValue}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select value' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='true'>True</SelectItem>
              <SelectItem value='false'>False</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'number':
        return (
          <Input
            type='number'
            value={condition.value || ''}
            min={0}
            onChange={(e) => updateValue(e.target.value)}
            placeholder='Enter value'
          />
        );
      case 'number-unit-days':
        if (condition.operator === 'compare_within_timestamp') {
          const currentValue = condition.value || {
            operator: 'gt',
            unit: 'days',
            unit_value: '',
          };
          return (
            <div className='space-y-3'>
              <Select
                value={currentValue.operator || 'gt'}
                onValueChange={(v) =>
                  updateValue({ ...currentValue, operator: v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Comparison' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='gt'>Greater than</SelectItem>
                  <SelectItem value='lt'>Less than</SelectItem>
                </SelectContent>
              </Select>
              <div className='flex gap-2'>
                <Input
                  type='number'
                  value={currentValue.unit_value || ''}
                  min={1}
                  step={1}
                  onChange={(e) =>
                    updateValue({ ...currentValue, unit_value: e.target.value })
                  }
                  placeholder='Value'
                  className='flex-1'
                />
                <Select
                  value={currentValue.unit || 'days'}
                  onValueChange={(v) =>
                    updateValue({ ...currentValue, unit: v })
                  }
                >
                  <SelectTrigger className='w-[100px]'>
                    <SelectValue placeholder='Unit' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='days'>Days</SelectItem>
                    <SelectItem value='hours'>Hours</SelectItem>
                    <SelectItem value='minutes'>Minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        } else {
          return (
            <div className='flex gap-2'>
              <Input
                type='number'
                value={condition.value || ''}
                min={1}
                step={1}
                onChange={(e) => updateValue(e.target.value)}
                placeholder='Enter days'
                className='flex-1'
              />
              <div className='flex items-center rounded-md border px-3 text-sm'>
                days ago
              </div>
            </div>
          );
        }
      default:
        return <Input placeholder='Value' disabled />;
    }
    return null;
  };

  const renderOperatorSelect = () => {
    const currentFieldInfo = fieldOptions.find(
      (opt) => opt.value === condition.field
    );
    if (!currentFieldInfo || currentFieldInfo.type === 'no-input') return null;
    let operators = [];
    switch (currentFieldInfo.type) {
      case 'owner-select':
      case 'tag-select':
        operators = [
          { value: 'equals', label: 'equals' },
          { value: 'in', label: 'is one of' },
        ];
        break;
      case 'status-select':
      case 'boolean':
        operators = [{ value: 'equals', label: 'equals' }];
        break;
      case 'number':
        operators = [
          { value: 'gt', label: 'greater than' },
          { value: 'lt', label: 'less than' },
        ];
        break;
      case 'number-unit-days':
        if (
          condition.field ===
          'contact_messages._prev_message_received_timestamp'
        ) {
          operators = [
            { value: 'compare_within_timestamp', label: 'custom comparison' },
          ];
        } else {
          operators = [
            { value: 'lt', label: 'less than (days ago)' },
            { value: 'gt', label: 'greater than (days ago)' },
          ];
        }
        break;
      default:
        return null;
    }

    return (
      <Select
        value={condition.operator}
        onValueChange={updateOperator}
        disabled={operators.length <= 1}
      >
        <SelectTrigger>
          <SelectValue placeholder='Select operator' />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.value} value={op.value}>
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const fieldInfo = fieldOptions.find((opt) => opt.value === condition.field);

  return (
    <Card className='mb-3 border-muted'>
      <CardContent className='p-3'>
        <div className='flex items-start justify-between'>
          <div className='flex-1 space-y-3 pr-2'>
            <div className='w-full'>
              <Label className='mb-1 block text-xs font-medium text-muted-foreground'>
                Field
              </Label>
              <Select value={condition.field} onValueChange={handleFieldChange}>
                <SelectTrigger>
                  <SelectValue placeholder='Select property' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {fieldOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            {fieldInfo && fieldInfo.type !== 'no-input' && (
              <div className='w-full'>
                <Label className='mb-1 block text-xs font-medium text-muted-foreground'>
                  Operator
                </Label>
                {renderOperatorSelect()}
              </div>
            )}
            {fieldInfo && fieldInfo.type !== 'no-input' && (
              <div className='w-full'>
                <Label className='mb-1 block text-xs font-medium text-muted-foreground'>
                  Value
                </Label>
                {renderValueInput()}
              </div>
            )}
            {fieldInfo && fieldInfo.type === 'no-input' && (
              <div className='mt-2 rounded-md border border-dashed bg-secondary/20 p-2 text-center text-xs text-muted-foreground'>
                Checks if no messages received since workflow start.
              </div>
            )}
          </div>
          <Button
            variant='ghost'
            size='icon'
            className='ml-1 mt-1 h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive'
            onClick={() =>
              handleFilterDelete(branchIndex, groupIndex, conditionIndex)
            }
            title='Delete condition'
          >
            <TrashIcon className='h-4 w-4' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
